
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, AfterViewInit, HostListener, inject } from "@angular/core";
import { Task } from "../../models";
import { DatePipe, NgClass, NgStyle } from "@angular/common";
import { TaskUtils } from "../../shared/task-utils";
import { CommonUtil } from "../../../common/utils/common.utils";
import { LocaleDatePipe } from "../pipes/locale-date.pipe";
import { LocaleService } from "../../../common/providers/locale.service";
import { getAuthUser, getTagList, TasksRootState } from "../../store";
import { Store } from "@ngrx/store";
import { take, takeWhile } from "rxjs/operators";
import { VNCActionWheelMenuService, VNCCheckboxComponent, VNCActionWheelMenuDirective } from "@uxf/vnc-library";
import { getUserProfile } from "src/app/reducers";
import { HeaderService } from "../../services/header.service";
import { VNCLongPressDirective } from "../../../shared/directives/long-press.directive";
import { UserAvatarComponent } from "../../../shared/components/user-avtar/user-avatar.component";
import { TranslatePipe } from "@ngx-translate/core";
import { MatIcon } from "@angular/material/icon";

@Component({
    selector: "vp-vnctask-list-item",
    templateUrl: "task-list-item.html",
    imports: [VNCLongPressDirective, NgClass, VNCCheckboxComponent, UserAvatarComponent, VNCActionWheelMenuDirective, MatIcon, NgStyle, TranslatePipe]
})
export class TaskListItemComponent implements OnDestroy, AfterViewInit , OnChanges {
  private changerDetectorRef = inject(ChangeDetectorRef);
  private localeService = inject(LocaleService);
  private store = inject<Store<TasksRootState>>(Store);
  private headerService = inject(HeaderService);
  private actionWheelMenuService = inject(VNCActionWheelMenuService);

  summaryWidth: number = 0;

  @Input() task: Task;
  @Output() onTaskClick = new EventEmitter<Task>();
  @Output() onTaskSelect = new EventEmitter<any>();
  currentDate: Date = new Date();
  private datePipe: LocaleDatePipe = new LocaleDatePipe(this.localeService);
  isLongPressed: boolean =  false;
  isAlive: boolean = true;
  user: any = {};
  currentUserEmail: string = "";
  tagFromStore = [];
  constructor() {
    this.currentDate.setHours(0, 0, 0, 0);
    this.headerService.sidebarChangeWidth$.pipe(takeWhile(() => this.isAlive)).subscribe(res=>{
      this.onResize();
    });

    this.store.select(getAuthUser).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
      if (user) {
        this.user = user;
        setTimeout( () => { this.changerDetectorRef.markForCheck(); }, 100);
      }
    });
    this.store
    .select(getTagList)
    .pipe(takeWhile(() => this.isAlive))
    .subscribe(tags => {
      if (tags) {
        this.tagFromStore = tags;
         this.updateTaskTag();
        this.changerDetectorRef.markForCheck();
      }
    });
    this.store.select(getUserProfile).pipe(take(1)).subscribe(user => {
      if (user) {
        this.currentUserEmail = user?.email;
        this.changerDetectorRef.markForCheck();
      }
    });
  }
  ngOnChanges() {
    this.updateTaskTag();
  }

  onSelectTask(state: boolean): void {
    this.task.isChecked = !this.task.isChecked;
    this.onTaskSelect.emit({
      state: this.task.isChecked,
      task: this.task
    });
  }

  dueDateDisplayFormat(): string {
    if (!this.task.due_date || ( (this.task.due_date) && (this.task.due_date.getTime() === TaskUtils.dueDateOrStartDateNull().getTime()))) {
      return "";
    }
    return this.datePipe.transform(this.task.due_date, "MMM dd");
  }

  dueDateDisplayYear(): string {
    if (!this.task.due_date || ( (this.task.due_date) && (this.task.due_date.getTime() === TaskUtils.dueDateOrStartDateNull().getTime()))) {
      return "";
    }
    return this.datePipe.transform(this.task.due_date, "yyyy");
  }

  private openContextMenu(): void {
    if (TaskUtils.isDeviceWidthOptimal()) {
      this.task.isChecked = !this.task.isChecked;
      this.isLongPressed = true;
      this.onSelectTask(this.task.isChecked);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const divSection = document.getElementById("flex-section-div" + this.task.id);
      this.summaryWidth = divSection.clientWidth - 30;
    }, );
  }

  @HostListener("window:resize")
  onResize() {
    this.summaryWidth = 0;
    setTimeout(() => {
      const divSection = document.getElementById("flex-section-div" + this.task.id);
      if (divSection) {
        this.summaryWidth = divSection.clientWidth - 30;
        this.changerDetectorRef.markForCheck();
      }

    }, 200);


  }

  onTaskClicked() {
    if (CommonUtil.isOnIOS() && this.isLongPressed) {
      this.isLongPressed = false;
      return;
    }
    this.onTaskClick.emit(this.task);
  }
  ngOnDestroy(): void {
    // Every subscription in this component is gated on takeWhile(() => this.isAlive),
    // so without this they outlive the component.
    this.isAlive = false;
    this.actionWheelMenuService.hideCircularMenu();
    (this.onTaskClick as any).unsubscribe();
    (this.onTaskSelect as any).unsubscribe();
  }
  getPriorityColor(item) {
    return TaskUtils.getPriorityColor(item);
  }

  getMenuDisabled(email) {
    if (email === this.user.email) {
      return {
        one: false,
        two: false,
        three: false,
        four: false
      };
    }
    return {};
  }

  updateTaskTag() {
    if (this.task?.tags && this.task.tags?.length > 0) {
      this.task.tags.forEach(tag => {
        this.store
          .select(getTagList)
          .pipe(takeWhile(() => this.isAlive))
          .subscribe(tags => {
            if (tags) {
              this.tagFromStore = tags;
              this.changerDetectorRef.markForCheck();
            }
        });
        const tagData = this.tagFromStore.find(t => t.id === tag.id);
        if (tagData) {
          tag["color_hex"] = tagData?.color_hex || "#a2aab3";
          this.changerDetectorRef.markForCheck();
        }
      });
    }
  }
}
